import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiCall, getApiUrl } from './Api';
import { getCurrentToken } from './SessionManager';
import { CreateModal } from './APISourcesTab';
import { ClassificationMappingEditor } from './ClassificationMappingEditor';
import { FileText, Webhook, Plus, Circle } from 'lucide-react';

const fmt = (v, fb = '0') =>
  v == null || v === '' || Number.isNaN(Number(v)) ? fb : Number(v).toLocaleString();
const fmtPct = (v, d = 1, fb = '0%') =>
  v == null || v === '' || Number.isNaN(Number(v)) ? fb : Number(v).toFixed(d) + '%';
const fmtMoney = (v, fb = '₹0') => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return fb;
  const n = Number(v);
  if (Math.abs(n) >= 10_000_000) return `₹${(n / 10_000_000).toFixed(2)}Cr`;
  if (Math.abs(n) >= 100_000)    return `₹${(n / 100_000).toFixed(2)}L`;
  if (Math.abs(n) >= 1_000)      return `₹${(n / 1_000).toFixed(1)}K`;
  return `₹${n.toLocaleString()}`;
};
const fmtSign = (v, d = 1, fb = '0') => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return fb;
  const n = Number(v);
  return (n >= 0 ? '+' : '') + n.toFixed(d);
};

const CampaignsTab = ({ session, user }) => {
  const sessionId = session.session_id;
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const [campsResp, outcomesResp] = await Promise.all([
        apiCall(`/api/campaigns?session_id=${sessionId}`).then(r => r.json()),
        apiCall(`/api/cdp/sessions/${sessionId}/campaign-outcomes`).then(r => r.json()),
      ]);

      const camps = campsResp.campaigns || campsResp || [];
      const outcomeList = outcomesResp.outcomes || outcomesResp || [];
      const byId = {};
      outcomeList.forEach(o => { byId[o.campaign_id] = o; });

      const enriched = camps.map(c => {
        const o = byId[c.campaign_id] || {};
        return {
          ...c,
          conversion_rate: o.conversion_rate ?? o.rates?.conversion_rate ?? 0,
          revenue:         o.total_revenue ?? o.financials?.total_revenue ?? 0,
          converted:       o.converted ?? o.funnel?.converted ?? 0,
        };
      }).sort((a, b) => (b.revenue || 0) - (a.revenue || 0));

      setCampaigns(enriched);
      if (!selectedId && enriched.length > 0) setSelectedId(enriched[0].campaign_id);
    } catch (e) {
      console.error('Failed to load campaigns:', e);
    } finally {
      setLoading(false);
    }
  }, [sessionId, selectedId]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleCreate = async (form) => {
    try {
      // POST to /api/campaigns with the CampaignCreate shape.
      // session_id goes in the body, not the URL.
      const resp = await apiCall(`/api/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          ...form,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      setShowNewModal(false);
      await loadCampaigns();
    } catch (e) {
      console.error('Create campaign failed:', e);
      alert(`Failed to create campaign: ${e.message || e}`);
    }
  };

  const handleUploadComplete = () => loadCampaigns();

  const totalRevenue = campaigns.reduce((s, c) => s + (c.revenue || 0), 0);

  if (loading) {
    return <div className="py-12 text-center text-sm text-gray-400">Loading campaigns…</div>;
  }

  if (campaigns.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-gray-500 mb-4">No campaigns yet.</p>
        <button
          onClick={() => setShowNewModal(true)}
          className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm hover:bg-gray-800"
        >
          + New campaign
        </button>
        {showNewModal && (
          <NewCampaignModal sessionId={sessionId} onClose={() => setShowNewModal(false)} onSubmit={handleCreate} />
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-medium text-gray-900">Campaigns</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {campaigns.length} campaigns · {fmtMoney(totalRevenue)} total revenue
          </p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm hover:bg-gray-800"
        >
          + New campaign
        </button>
      </div>

      <div className="grid grid-cols-[220px_1fr] gap-4">
        <CampaignList
          campaigns={campaigns}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        {selectedId && (
          <CampaignDetail
            sessionId={sessionId}
            campaignId={selectedId}
            session={session}
            user={user}
            onRefresh={handleUploadComplete}
          />
        )}
      </div>

      {showNewModal && (
        <NewCampaignModal sessionId={sessionId} onClose={() => setShowNewModal(false)} onSubmit={handleCreate} />
      )}
    </div>
  );
};

const CampaignList = ({ campaigns, selectedId, onSelect }) => (
  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
    <div className="px-3.5 py-2.5 text-[11px] text-gray-400 border-b border-gray-200">
      Sorted by revenue
    </div>
    {campaigns.map(c => {
      const isSelected = c.campaign_id === selectedId;
      const convColor =
        c.conversion_rate >= 50 ? 'text-emerald-600' :
        c.conversion_rate >= 25 ? 'text-amber-700'   :
        c.conversion_rate > 0   ? 'text-red-700'     : 'text-gray-400';
      return (
        <button
          key={c.campaign_id}
          onClick={() => onSelect(c.campaign_id)}
          className={`w-full text-left px-3.5 py-3 border-b border-gray-200 last:border-b-0 transition-colors ${
            isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
          }`}
        >
          <div className="text-sm font-medium text-gray-900 truncate">{c.campaign_name}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            {c.campaign_id} · {c.channel || '—'}
          </div>
          <div className="flex gap-2.5 mt-1.5 text-[11px]">
            <span className={convColor}>{fmtPct(c.conversion_rate, 0)} conv</span>
            <span className="text-gray-500">{fmtMoney(c.revenue)}</span>
          </div>
        </button>
      );
    })}
  </div>
);

const CampaignDetail = ({ sessionId, campaignId, session, user, onRefresh }) => {
  const [campaign, setCampaign] = useState(null);
  const [outcome, setOutcome]   = useState(null);
  const [events, setEvents]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [sourcesRefreshKey, setSourcesRefreshKey] = useState(0);
 
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [campResp, outcomeResp, eventsResp] = await Promise.all([
        apiCall(`/api/campaigns/${campaignId}`).then(r => r.json()),
        apiCall(`/api/cdp/sessions/${sessionId}/campaign-outcomes/${campaignId}`).then(r => r.json()),
        apiCall(`/api/cdp/sessions/${sessionId}/events?campaign_id=${campaignId}&limit=10`)
          .then(r => r.json()).catch(() => ({ events: [], total: 0 })),
      ]);
      setCampaign(campResp);
      setOutcome(outcomeResp?.computed ? outcomeResp : null);
      setEvents(eventsResp);
    } catch (e) {
      console.error('Failed to load campaign detail:', e);
    } finally {
      setLoading(false);
    }
  }, [sessionId, campaignId]);
 
  useEffect(() => { load(); }, [load]);
 
  const handleUploadDone = async () => {
    await load();
    setSourcesRefreshKey(k => k + 1);
    onRefresh?.();
  };
 
  const handleAPISourceCreated = async (credentials) => {
    setShowConnectModal(false);
    setCreatedCredentials(credentials);  // show credentials once to user
    setSourcesRefreshKey(k => k + 1);
    onRefresh?.();
  };
 
  if (loading || !campaign) {
    return <div className="py-8 text-center text-sm text-gray-400">Loading…</div>;
  }
 
  return (
    <div className="space-y-4">
      <CampaignHeader campaign={campaign} outcome={outcome} />
      <FunnelCard outcome={outcome} />
 
      <DataSourcesPanel
        sessionId={sessionId}
        campaignId={campaignId}
        refreshKey={sourcesRefreshKey}
        onConnectAPI={() => setShowConnectModal(true)}
      />
 
      <UploadPanel
        sessionId={sessionId}
        campaignId={campaignId}
        onDone={handleUploadDone}
      />
 
      <RecentEventsCard events={events} />
 
      {showConnectModal && (
        <CreateModal
          session={session}
          cdpState="versioned"
          sourceFilter="campaign"
          defaultCampaignId={campaignId}
          onClose={() => setShowConnectModal(false)}
          onSuccess={handleAPISourceCreated}
        />
      )}
 
      {createdCredentials && (
        <APICredentialsDisplay
          credentials={createdCredentials}
          onClose={() => setCreatedCredentials(null)}
        />
      )}
    </div>
  );
};

const CampaignHeader = ({ campaign, outcome }) => {
  const { campaign_name, campaign_id, channel, cost, launched_at } = campaign;
  const funnel     = outcome?.funnel || {};
  const financials = outcome?.financials || {};
  const roi        = financials.roi_percentage || 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg px-5 py-4">
      <div className="flex items-baseline justify-between">
        <h4 className="text-base font-medium text-gray-900">{campaign_name}</h4>
        {launched_at && (
          <span className="text-[11px] text-gray-400">
            launched {new Date(launched_at).toLocaleDateString()}
          </span>
        )}
      </div>
      <div className="flex gap-3 text-xs text-gray-500 mt-1 mb-3 flex-wrap">
        <span>{campaign_id}</span>
        <span>·</span>
        <span>{channel || 'No channel'}</span>
        <span>·</span>
        <span>Cost {fmtMoney(cost)}</span>
        {outcome && roi !== 0 && (
          <>
            <span>·</span>
            <span className={roi >= 0 ? 'text-emerald-600' : 'text-red-600'}>
              ROI {fmtSign(roi, 0)}%
            </span>
          </>
        )}
      </div>

      <div className="grid grid-cols-4 gap-2">
        <StatTile label="Targeted"  value={fmt(funnel.targeted)} />
        <StatTile label="Converted" value={fmt(funnel.converted)} />
        <StatTile label="Revenue"   value={fmtMoney(financials.total_revenue)} />
        <StatTile label="Avg order" value={fmtMoney(financials.avg_order_value)} />
      </div>
    </div>
  );
};

const StatTile = ({ label, value }) => (
  <div className="bg-gray-50 rounded-md px-3 py-2.5">
    <div className="text-[11px] text-gray-500">{label}</div>
    <div className="text-lg font-medium text-gray-900 mt-0.5">{value}</div>
  </div>
);

const FunnelCard = ({ outcome }) => {
  if (!outcome) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-5 py-8 text-center">
        <div className="text-sm text-gray-500">No outcome computed yet</div>
        <div className="text-xs text-gray-400 mt-1">Upload campaign data below to see the funnel</div>
      </div>
    );
  }

  const funnel = outcome.funnel || {};
  const rates  = outcome.rates  || {};
  const steps = [
    { label: 'Targeted',  value: funnel.targeted,  rate: null },
    { label: 'Delivered', value: funnel.delivered, rate: rates.delivery_rate },
    { label: 'Opened',    value: funnel.opened,    rate: rates.open_rate },
    { label: 'Clicked',   value: funnel.clicked,   rate: rates.click_rate },
    { label: 'Converted', value: funnel.converted, rate: rates.conversion_rate },
  ];
  const maxVal = Math.max(...steps.map(s => s.value || 0), 1);

  return (
    <div className="bg-white border border-gray-200 rounded-lg px-5 py-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-medium text-gray-500">Conversion funnel</div>
        {outcome.computed_at && (
          <div className="text-[11px] text-gray-400">
            computed {new Date(outcome.computed_at).toLocaleString()}
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        {steps.map((step, i) => {
          const pct = maxVal ? (step.value || 0) / maxVal * 100 : 0;
          const isFinal = i === steps.length - 1;
          const hasData = step.value > 0;
          const barColor = hasData
            ? (isFinal ? 'bg-emerald-500' : i <= 1 ? 'bg-blue-300' : 'bg-emerald-300')
            : 'bg-gray-100';
          const textColor = hasData
            ? (isFinal ? 'text-emerald-950' : i <= 1 ? 'text-blue-900' : 'text-emerald-900')
            : 'text-gray-400';
          return (
            <div key={step.label} className="flex items-center gap-2.5">
              <div className="w-16 text-[11px] text-gray-500 text-right">{step.label}</div>
              <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden relative">
                <div className={`h-full ${barColor}`} style={{ width: `${Math.max(pct, 2)}%` }} />
                <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] font-medium ${textColor}`}>
                  {fmt(step.value)}
                </span>
              </div>
              <div className="w-11 text-[11px] text-right text-emerald-600">
                {step.rate != null ? fmtPct(step.rate, 0) : '—'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// UPLOAD PANEL — two-step flow
// ═══════════════════════════════════════════════════════════════════════════
// Flow: file → auto-detect → ClassificationMappingEditor → confirmed upload
//
// The second step is mandatory (not just a low-confidence fallback). Why:
// classification mappings are the projection that turns the wide CSV into
// narrow event rows in cdp_campaign_events. If the user doesn't confirm
// them, we might extract with the wrong customer_key column and lose every
// event's identity. Better one extra click than silent data loss.
//
// After confirm, we upload via /upload-subsequent with upload_type=
// 'campaign_conversion' — that endpoint stages the file directly into
// cdp_campaign_file_staging_{file_id} and projects into cdp_campaign_events,
// completely bypassing cdp_unified.

const UploadPanel = ({ sessionId, campaignId, onDone }) => {
  const [state, setState] = useState({ status: 'idle' });
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    try {
      setState({ status: 'reading', filename: file.name, stage: 'Reading columns…', progress: 20 });

      const { columns, sample_rows } = await readCsvSample(file);

      setState({ status: 'reading', filename: file.name, stage: 'Auto-detecting format…', progress: 60 });

      const detect = await apiCall(
        `/api/cdp/sessions/${sessionId}/classify/auto-detect`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source_type: 'file',
            source_id: 'pending',
            columns,
            sample_rows,
          }),
        }
      ).then(r => r.json());

      // Campaign-aware fallback: inside Campaigns tab, if detector says
      // customer_profile we bias toward the stronger campaign signal.
      let effective = { ...detect };
      if (effective.intent === 'customer_profile') {
        const scores = effective.all_scores || {};
        const convScore = scores.campaign_conversion || 0;
        const respScore = scores.campaign_response || 0;
        effective.intent = (convScore > respScore && convScore > 0)
          ? 'campaign_conversion'
          : 'campaign_response';
        effective.confidence = Math.max(effective.confidence || 0, 0.4);
        effective.fallback_applied = true;
      }

      // Always go through the mapping editor — never silently upload.
      setState({
        status: 'classifying',
        filename: file.name,
        pendingFile: file,
        columns,
        sample_rows,
        detected: effective,
      });
    } catch (err) {
      console.error(err);
      setState({ status: 'error', filename: file.name, error: err.message || 'Upload failed' });
    }
  };

  const handleConfirmMapping = async ({ intent, column_roles, metric_definitions }) => {
    const { pendingFile } = state;
    try {
      setState({
        status: 'uploading',
        filename: pendingFile.name,
        stage: 'Uploading file…',
        progress: 30,
        detected: { intent, column_roles },
      });

      const formData = new FormData();
      formData.append('files', pendingFile);
      formData.append('upload_type', 'campaign_conversion');
      formData.append('campaign_id', campaignId);
      formData.append('data_intent', intent);
      formData.append('column_roles_json', JSON.stringify(column_roles));
      formData.append('metric_definitions_json', JSON.stringify(metric_definitions || []));

      const uploadResp = await fetch(
        `${getApiUrl()}/api/cdp/sessions/${sessionId}/upload-subsequent`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${getCurrentToken()}` },
          body: formData,
        }
      );

      if (!uploadResp.ok) {
        const errText = await uploadResp.text();
        throw new Error(errText || `Upload failed: ${uploadResp.statusText}`);
      }

      setState(prev => ({ ...prev, stage: 'Staging rows…', progress: 55 }));
      setState(prev => ({ ...prev, stage: 'Extracting events…', progress: 75 }));

      await pollForOutcome(sessionId, campaignId, (stage, progress) =>
        setState(prev => ({ ...prev, stage, progress }))
      );

      setState({ status: 'success', filename: pendingFile.name });
      await onDone?.();
      setTimeout(() => setState({ status: 'idle' }), 3000);
    } catch (err) {
      console.error(err);
      setState({ status: 'error', filename: pendingFile.name, error: err.message || 'Upload failed' });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  if (state.status === 'classifying') {
    return (
      <ClassificationMappingEditor
        filename={state.filename}
        columns={state.columns}
        sampleRows={state.sample_rows}
        detected={state.detected}
        onCancel={() => setState({ status: 'idle' })}
        onConfirm={handleConfirmMapping}
      />
    );
  }

  if (state.status === 'reading' || state.status === 'uploading') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-5 py-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-900">{state.filename}</div>
          {state.detected && (
            <div className="text-[11px] text-gray-500">
              loaded as {state.detected.intent?.replace('campaign_', '')}
            </div>
          )}
        </div>
        <div className="w-full h-1.5 bg-gray-100 rounded overflow-hidden">
          <div
            className="h-full bg-blue-500 transition-all duration-500"
            style={{ width: `${state.progress || 5}%` }}
          />
        </div>
        <div className="text-xs text-gray-500 mt-2">{state.stage}</div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg px-5 py-4">
        <div className="text-sm font-medium text-red-900">Upload failed</div>
        <div className="text-xs text-red-700 mt-1 whitespace-pre-wrap">{state.error}</div>
        <button
          onClick={() => setState({ status: 'idle' })}
          className="text-xs text-red-600 underline mt-2"
        >
          Try again
        </button>
      </div>
    );
  }

  if (state.status === 'success') {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-5 py-4">
        <div className="text-sm font-medium text-emerald-900">Upload complete</div>
        <div className="text-xs text-emerald-700 mt-1">Funnel refreshed above</div>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      className={`bg-white border border-dashed rounded-lg px-5 py-6 text-center cursor-pointer transition-colors ${
        dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
      }`}
    >
      <input
        type="file"
        accept=".csv,.xlsx,.xls"
        ref={fileInputRef}
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
      />
      <div className="text-sm text-gray-900">Drop campaign data CSV here</div>
      <div className="text-[11px] text-gray-500 mt-1">
        You'll confirm the classification mappings before it lands
      </div>
      <div className="inline-block mt-3 px-3.5 py-1.5 bg-gray-100 rounded text-xs text-gray-600">
        or click to browse
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ClassificationMappingEditor lives in ./ClassificationMappingEditor.js —
// imported at the top of this file and re-used by APISourcesTab.
// ═══════════════════════════════════════════════════════════════════════════



// Read a sample of the uploaded file for auto-detect. The sample size
// needs to be generous enough that value-based role inference (e.g.
// conversion_flag from event_type values) has a chance to see the rarer
// row types — campaign CSVs commonly group delivered rows at the top
// and converted rows at the bottom. 200 rows within 256 KB is a
// reasonable floor for channel-funnel files; larger and we start
// paying real parse time on the main thread.
async function readCsvSample(file, maxRows = 200) {
  const text = await file.slice(0, 256 * 1024).text();
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length === 0) return { columns: [], sample_rows: [] };

  const parseRow = (line) =>
    line.split(',').map(s => s.trim().replace(/^"|"$/g, ''));

  const columns = parseRow(lines[0]);
  const sample_rows = lines.slice(1, 1 + maxRows).map(line => {
    const cells = parseRow(line);
    const row = {};
    columns.forEach((c, i) => { row[c] = cells[i] ?? ''; });
    return row;
  });

  return { columns, sample_rows };
}

async function pollForOutcome(sessionId, campaignId, onProgress, opts = {}) {
  const startTs = Date.now();
  const timeoutMs = opts.timeoutMs ?? 60_000;
  let lastComputedAt = null;
  let attempt = 0;

  while (Date.now() - startTs < timeoutMs) {
    attempt += 1;
    const progress = Math.min(75 + attempt * 2, 95);
    onProgress?.(`Computing funnel… (${attempt})`, progress);

    try {
      const resp = await apiCall(
        `/api/cdp/sessions/${sessionId}/campaign-outcomes/${campaignId}`
      ).then(r => r.json());

      if (resp.computed && resp.computed_at) {
        const ts = new Date(resp.computed_at).getTime();
        if (!lastComputedAt) lastComputedAt = ts;
        if (ts > lastComputedAt || attempt > 3) {
          onProgress?.('Done', 100);
          return;
        }
      }
    } catch { }
    await new Promise(r => setTimeout(r, 1500));
  }

  onProgress?.('Timed out waiting for merge', 100);
}

const RecentEventsCard = ({ events }) => {
  if (!events?.events?.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-5 py-4">
        <div className="text-xs font-medium text-gray-500 mb-2">Recent events</div>
        <div className="text-xs text-gray-400 py-4 text-center">
          No events yet — upload a CSV to get started
        </div>
      </div>
    );
  }

  const typeBadge = (type) => {
    const styles = {
      conversion: 'bg-emerald-100 text-emerald-700',
      converted:  'bg-emerald-100 text-emerald-700',
      purchase:   'bg-emerald-100 text-emerald-700',
      purchased:  'bg-emerald-100 text-emerald-700',
      clicked:    'bg-blue-100 text-blue-700',
      click:      'bg-blue-100 text-blue-700',
      opened:     'bg-gray-100 text-gray-700',
      open:       'bg-gray-100 text-gray-700',
      delivered:  'bg-gray-100 text-gray-600',
    };
    return styles[type?.toLowerCase()] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg px-5 py-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-gray-500">Recent events</div>
        <span className="text-[11px] text-gray-400">{events.total} events total</span>
      </div>
      <div className="space-y-1">
        {events.events.map((e, i) => (
          <div key={i} className="flex items-center justify-between text-xs px-2 py-1.5 bg-gray-50 rounded">
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${typeBadge(e.event_type)}`}>
              {e.event_type}
            </span>
            <span className="text-gray-900 flex-1 text-center px-2">
              {e.customer_key}{e.event_value > 0 ? ` · ${fmtMoney(e.event_value)}` : ''}
            </span>
            <span className="text-gray-400 text-[11px]">
              {e.event_timestamp && new Date(e.event_timestamp).toLocaleDateString(undefined, {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// NEW CAMPAIGN MODAL
// ═══════════════════════════════════════════════════════════════════════════
// Form shape matches /api/campaigns CampaignCreate:
//   session_id (injected by parent), segment_id (optional picker),
//   campaign_name, description, primary_channel (enum: whatsapp|rcs|sms|email|voice),
//   fallback_channels, message_template, schedule_time, sample_percentage,
//   conversion_tracking_enabled, budget_limit, cost_per_unit
//
// Segment is optional — you can draft a campaign and attach a segment later.
// The backend's execute_campaign should refuse to run until one is set.

const CHANNEL_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms',      label: 'SMS' },
  { value: 'email',    label: 'Email' },
  { value: 'rcs',      label: 'RCS' },
  { value: 'voice',    label: 'Voice' },
];

const NewCampaignModal = ({ sessionId, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    campaign_name: '',
    description: '',
    segment_id: '',           // '' means no segment
    primary_channel: 'whatsapp',
    fallback_channels: [],
    message_template: '',
    schedule_time: '',        // '' → null on submit
    sample_percentage: 100,
    conversion_tracking_enabled: true,
    budget_limit: '',         // '' → null on submit
    cost_per_unit: 0.05,
  });
  const [segments, setSegments] = useState([]);
  const [loadingSegments, setLoadingSegments] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load segments for this session so users can optionally pick one.
  // Failure here is non-fatal — the dropdown just stays empty.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiCall(`/api/cdp/sessions/${sessionId}/segments`);
        const data = await resp.json();
        if (!cancelled) {
          setSegments(data.segments || data || []);
        }
      } catch (e) {
        console.warn('Could not load segments:', e);
      } finally {
        if (!cancelled) setLoadingSegments(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // CampaignCreate requires campaign_name, primary_channel, message_template.
  // Everything else is optional or has a default.
  const canSubmit =
    form.campaign_name.trim().length > 0 &&
    form.message_template.trim().length > 0 &&
    form.primary_channel &&
    !submitting;

  const toggleFallback = (channel) => {
    setForm(f => ({
      ...f,
      fallback_channels: f.fallback_channels.includes(channel)
        ? f.fallback_channels.filter(c => c !== channel)
        : [...f.fallback_channels, channel],
    }));
  };

  const buildPayload = () => {
    // Map the form to the exact CampaignCreate shape. Empty strings on
    // optional fields become null so Pydantic falls back to defaults.
    const p = {
      campaign_name: form.campaign_name.trim(),
      description: form.description.trim() || null,
      primary_channel: form.primary_channel,
      fallback_channels: form.fallback_channels.filter(c => c !== form.primary_channel),
      message_template: form.message_template,
      sample_percentage: Number(form.sample_percentage) || 100,
      conversion_tracking_enabled: form.conversion_tracking_enabled,
    };
    if (form.segment_id) p.segment_id = form.segment_id;
    if (form.schedule_time) p.schedule_time = new Date(form.schedule_time).toISOString();
    if (form.budget_limit !== '' && form.budget_limit !== null) {
      p.budget_limit = Number(form.budget_limit);
    }
    if (form.cost_per_unit !== '' && form.cost_per_unit !== null) {
      p.cost_per_unit = Number(form.cost_per_unit);
    }
    return p;
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(buildPayload());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
      >
        <h3 className="text-base font-medium text-gray-900 mb-1">New campaign</h3>
        <p className="text-xs text-gray-500 mb-4">
          Draft the campaign now, attach a segment and upload data later.
        </p>

        <div className="space-y-3">
          {/* ── Required ── */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Campaign name <span className="text-red-500">*</span>
            </label>
            <input
              value={form.campaign_name}
              onChange={(e) => setForm({ ...form, campaign_name: e.target.value })}
              placeholder="Summer Sale 2025"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Primary channel <span className="text-red-500">*</span>
              </label>
              <select
                value={form.primary_channel}
                onChange={(e) => setForm({ ...form, primary_channel: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
              >
                {CHANNEL_OPTIONS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Segment
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <select
                value={form.segment_id}
                onChange={(e) => setForm({ ...form, segment_id: e.target.value })}
                disabled={loadingSegments}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white disabled:bg-gray-50"
              >
                <option value="">— attach later —</option>
                {segments.map(s => (
                  <option key={s.segment_id} value={s.segment_id}>
                    {s.segment_name}
                    {s.customer_count != null ? ` (${s.customer_count.toLocaleString()})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Message template <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.message_template}
              onChange={(e) => setForm({ ...form, message_template: e.target.value })}
              placeholder="Hi {{name}}, …"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
            />
            <div className="text-[10px] text-gray-400 mt-0.5">
              Use <code>{'{{field_name}}'}</code> for customer placeholders
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>

          {/* ── Advanced (collapsed by default) ── */}
          <button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            {showAdvanced ? '▾' : '▸'} Advanced options
          </button>

          {showAdvanced && (
            <div className="space-y-3 pt-2 border-t border-gray-100">
              {/* Fallback channels */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Fallback channels
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {CHANNEL_OPTIONS
                    .filter(c => c.value !== form.primary_channel)
                    .map(c => {
                      const on = form.fallback_channels.includes(c.value);
                      return (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => toggleFallback(c.value)}
                          className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                            on
                              ? 'bg-blue-50 border-blue-300 text-blue-700'
                              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {c.label}
                        </button>
                      );
                    })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Schedule time
                  </label>
                  <input
                    type="datetime-local"
                    value={form.schedule_time}
                    onChange={(e) => setForm({ ...form, schedule_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    Leave blank to keep as draft
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Sample %
                  </label>
                  <input
                    type="number"
                    min="0.1"
                    max="100"
                    step="0.1"
                    value={form.sample_percentage}
                    onChange={(e) => setForm({ ...form, sample_percentage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Budget limit (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.budget_limit}
                    onChange={(e) => setForm({ ...form, budget_limit: e.target.value })}
                    placeholder="No limit"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Cost per unit (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.cost_per_unit}
                    onChange={(e) => setForm({ ...form, cost_per_unit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="conv-tracking"
                  type="checkbox"
                  checked={form.conversion_tracking_enabled}
                  onChange={(e) => setForm({
                    ...form,
                    conversion_tracking_enabled: e.target.checked,
                  })}
                  className="h-3.5 w-3.5"
                />
                <label htmlFor="conv-tracking" className="text-xs text-gray-700">
                  Track conversions for this campaign
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-5 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-40"
          >
            {submitting ? 'Creating…' : 'Create campaign'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DataSourcesPanel = ({ sessionId, campaignId, onConnectAPI, refreshKey }) => {
  const [sources, setSources] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
 
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiCall(
        `/api/cdp/sessions/${sessionId}/campaigns/${campaignId}/sources`
      ).then(res => res.json());
      setSources(r.sources || []);
      setSummary(r.summary);
    } catch (e) {
      console.error('Failed to load campaign sources:', e);
    } finally {
      setLoading(false);
    }
  }, [sessionId, campaignId]);
 
  useEffect(() => { load(); }, [load, refreshKey]);
 
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-5 py-4">
        <div className="text-xs text-gray-400">Loading data sources…</div>
      </div>
    );
  }
 
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-5 py-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-medium text-gray-500">Data sources</div>
          {summary && (
            <div className="text-[11px] text-gray-400 mt-0.5">
              {summary.file_sources} file{summary.file_sources !== 1 ? 's' : ''} · {summary.api_sources} API{summary.api_sources !== 1 ? 's' : ''}
              {summary.total_events > 0 && ` · ${summary.total_events.toLocaleString()} records`}
            </div>
          )}
        </div>
        <button
          onClick={onConnectAPI}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded text-xs border border-gray-200"
        >
          <Webhook className="w-3 h-3" />
          Connect API
        </button>
      </div>
 
      {sources.length === 0 ? (
        <div className="py-3 text-center text-xs text-gray-400">
          No sources yet — upload a CSV below or connect an API
        </div>
      ) : (
        <div className="space-y-1">
          {sources.map((s) => (
            <SourceRow key={`${s.source_type}-${s.source_id}`} source={s} />
          ))}
        </div>
      )}
    </div>
  );
};

const SourceRow = ({ source }) => {
  const Icon = source.source_type === 'file' ? FileText : Webhook;
  const statusColor =
    source.status === 'active' ? 'text-emerald-500' :
    source.status === 'paused' ? 'text-amber-500' :
    source.status === 'pending' ? 'text-gray-400' :
    'text-red-500';
 
  const intentLabel = source.data_intent?.replace('campaign_', '') || 'unknown';
  const intentBadge =
    source.data_intent === 'campaign_response' ? 'bg-blue-100 text-blue-700' :
    source.data_intent === 'campaign_conversion' ? 'bg-emerald-100 text-emerald-700' :
    'bg-gray-100 text-gray-600';
 
  return (
    <div className="flex items-center gap-3 py-2 px-2 rounded hover:bg-gray-50">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Circle className={`w-2 h-2 fill-current ${statusColor}`} />
        <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <div className="text-sm text-gray-900 truncate">{source.source_name}</div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${intentBadge}`}>
          {intentLabel}
        </span>
      </div>
      <div className="flex items-center gap-3 text-[11px] text-gray-500 flex-shrink-0">
        {source.record_count > 0 && (
          <span>{source.record_count.toLocaleString()} records</span>
        )}
        {source.last_ingested_at && (
          <span>{new Date(source.last_ingested_at).toLocaleDateString(undefined, {
            month: 'short', day: 'numeric'
          })}</span>
        )}
      </div>
    </div>
  );
};

const APICredentialsDisplay = ({ credentials, onClose }) => {
  const [copied, setCopied] = useState(null);
 
  const copy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch { }
  };
 
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg max-w-xl w-full mx-4 p-6"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-medium text-gray-900">
              API source connected
            </h3>
            <p className="text-xs text-amber-700 mt-1">
              ⚠ Save the API secret now — it won't be shown again.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-2">×</button>
        </div>
 
        <div className="space-y-3">
          <CredentialRow
            label="Ingest URL"
            value={credentials.ingest_url || credentials.webhook_url || ''}
            onCopy={copy}
            copied={copied === 'url'}
            copyKey="url"
          />
          <CredentialRow
            label="API key"
            value={credentials.api_key || ''}
            onCopy={copy}
            copied={copied === 'key'}
            copyKey="key"
          />
          <CredentialRow
            label="API secret"
            value={credentials.api_secret || ''}
            onCopy={copy}
            copied={copied === 'secret'}
            copyKey="secret"
            mask
          />
        </div>
 
        <div className="flex justify-end mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm hover:bg-gray-800"
          >
            I've saved these
          </button>
        </div>
      </div>
    </div>
  );
};
 
const CredentialRow = ({ label, value, onCopy, copied, copyKey, mask }) => {
  const [revealed, setRevealed] = useState(!mask);
  const display = mask && !revealed ? '•'.repeat(Math.min(value.length, 24)) : value;
 
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-xs font-mono text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap">
          {display}
        </code>
        {mask && (
          <button
            onClick={() => setRevealed(!revealed)}
            className="px-2 py-2 text-xs text-gray-600 hover:bg-gray-50 rounded border border-gray-200"
          >
            {revealed ? 'Hide' : 'Show'}
          </button>
        )}
        <button
          onClick={() => onCopy(value, copyKey)}
          className="px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 rounded border border-gray-200"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
};

export default CampaignsTab;
